import React from "react";
import { Handle, Position } from "@xyflow/react";
import {
  Zap,
  MessageSquare,
  Clock,
  GitBranch,
  Globe,
  Tag,
  Users,
  StickyNote,
  Sparkles,
  ListOrdered,
  HelpCircle,
  Image,
  Shuffle,
  UserCheck,
  CircleCheck,
  Layers,
  Ban,
  Columns3,
  Plug,
  Mic,
  Video,
  FileText,
  Variable,
  CornerUpLeft,
  MousePointerClick,
  List,
  Flag,
} from "lucide-react";

import { cn } from "../../lib/utils";

// Cor por família de bloco, como no Typebot: fala com o cliente é azul,
// pergunta é laranja, caminho é roxo, atendimento é verde, integração é
// cinza-azulado. A mesma cor aparece no item da barra lateral, então a pessoa
// reconhece o bloco antes de ler o nome.
export const ACENTOS = {
  fala: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
  pergunta:
    "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
  caminho:
    "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
  atendimento:
    "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
  integracao:
    "bg-slate-200 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300",
  gatilho:
    "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
};

// Cartão do bloco. O conteúdo fica numa caixinha interna, e não solto no
// cartão: é o que dá ao Typebot a leitura de "bloco dentro do bloco" e separa
// o que a pessoa configurou do nome do bloco.
const shell =
  "w-[230px] rounded-xl border border-border bg-card p-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all";

const NodeShell = ({ selected, accent, icon: Icon, title, summary, children }) => (
  <div
    className={cn(
      shell,
      selected
        ? "border-primary/60 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
        : "hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
    )}
  >
    <div className="flex items-center gap-2 px-1.5 py-1">
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-md",
          accent
        )}
      >
        <Icon className="h-3 w-3" />
      </span>
      <span className="truncate text-[13px] font-medium leading-none">
        {title}
      </span>
    </div>

    {children || (
      <div className="mt-0.5 rounded-lg bg-muted/60 px-2.5 py-1.5">
        <p className="truncate text-xs leading-relaxed text-muted-foreground">
          {summary || "Clique para configurar"}
        </p>
      </div>
    )}
  </div>
);

// Saída nomeada dentro do cartão. No Typebot cada caminho é uma linha com o
// conector na borda direita — muito mais legível do que rótulos soltos
// embaixo do bloco, que era o que havia aqui.
const Saida = ({ id, texto, tom }) => (
  <div className="relative mt-1 flex items-center justify-between rounded-lg border bg-background px-2.5 py-1.5">
    <span
      className={cn(
        "truncate text-xs",
        tom === "positivo" && "text-emerald-600 dark:text-emerald-400",
        tom === "negativo" && "text-rose-600 dark:text-rose-400",
        !tom && "text-muted-foreground"
      )}
    >
      {texto}
    </span>
    <Handle
      type="source"
      position={Position.Right}
      id={id}
      aria-label={`Saída ${texto}`}
      style={{ top: "50%", right: -7 }}
    />
  </div>
);

// O card precisa dizer por que este fluxo começa — com palavra-chave, quais
// são as palavras é a informação que importa de relance.
const resumoDoGatilho = (data) => {
	const origem = (() => {
		if (data.triggerType === "webhook") return "Via webhook";

		if (data.triggerType === "keyword") {
			const palavras = Array.isArray(data.keywords) ? data.keywords : [];

			if (palavras.length === 0) return "Palavra-chave (nenhuma definida)";

			return `Palavra-chave: ${palavras.slice(0, 3).join(", ")}${
				palavras.length > 3 ? "…" : ""
			}`;
		}

		return "Manual";
	})();

	// Grupo é a informação que mais assusta quando passa despercebida: o card
	// avisa antes de alguém descobrir pelo grupo do cliente.
	if (data.escopo === "grupo") return `${origem} · em grupos`;
	if (data.escopo === "ambos") return `${origem} · individual e grupos`;

	return origem;
};

export const TriggerNode = ({ data, selected }) => (
  <>
    <NodeShell
      selected={selected}
      accent={ACENTOS.gatilho}
      icon={Zap}
      title="Gatilho"
      summary={resumoDoGatilho(data)}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const SendMessageNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.fala}
      icon={MessageSquare}
      title="Enviar mensagem"
      summary={data.number ? `Para ${data.number}` : "Sem destinatário"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const DelayNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.caminho}
      icon={Clock}
      title="Atraso"
      summary={`Aguardar ${data.seconds || 0}s`}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const ConditionNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.caminho}
      icon={GitBranch}
      title="Condição"
    >
      <div className="mt-0.5 rounded-lg bg-muted/60 px-2.5 py-1.5">
        <p className="truncate text-xs leading-relaxed text-muted-foreground">
          {data.field
            ? `${data.field} ${data.operator || "equals"} ${data.value ?? ""}`
            : "Clique para definir a regra"}
        </p>
      </div>
      <Saida id="true" texto="Se sim" tom="positivo" />
      <Saida id="false" texto="Se não" tom="negativo" />
    </NodeShell>
  </>
);

export const HttpRequestNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.integracao}
      icon={Globe}
      title="Requisição HTTP"
      summary={data.url ? `${data.method || "GET"} ${data.url}` : "Sem URL"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const AddTagNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.atendimento}
      icon={Tag}
      title={data.acao === "remover" ? "Retirar etiqueta" : "Aplicar etiqueta"}
      summary={
        data.tagName
          ? `${data.tagName}${data.criarSeNaoExistir ? " (cria se faltar)" : ""}`
          : "Escolha a etiqueta"
      }
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const TransferQueueNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.atendimento}
      icon={Users}
      title="Transferir para fila"
      summary={data.queueName || "Escolha a fila"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const NoteNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.atendimento}
      icon={StickyNote}
      title="Nota interna"
      summary={data.note || "Sem texto"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const AiNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.fala}
      icon={Sparkles}
      title="Inteligência Artificial"
      summary={data.prompt || "Sem instrução"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

// Menu: uma saída por opção, mais a saída de desistência. Elas ficam na
// direita porque na base não caberiam lado a lado quando o menu é longo.
export const MenuNode = ({ data, selected }) => {
  const opcoes = Array.isArray(data.options) ? data.options : [];

  return (
    <>
      <Handle type="target" position={Position.Top} aria-label="Entrada" />
      <NodeShell
        selected={selected}
        accent={ACENTOS.pergunta}
        icon={ListOrdered}
        title="Menu"
      >
        <div className="mt-0.5 rounded-lg bg-muted/60 px-2.5 py-1.5">
          <p className="truncate text-xs leading-relaxed text-muted-foreground">
            {data.message || "Clique para escrever a pergunta"}
          </p>
        </div>

        {opcoes.length === 0 ? (
          <p className="mt-1 px-2.5 py-1.5 text-xs text-muted-foreground">
            Nenhuma opção ainda
          </p>
        ) : (
          opcoes.map((opcao, i) => (
            <Saida
              key={`${opcao.number}-${i}`}
              id={`opt-${opcao.number}`}
              texto={`${opcao.number}. ${opcao.label || "sem texto"}`}
            />
          ))
        )}

        <Saida id="fallback" texto="Se desistir" />
      </NodeShell>
    </>
  );
};

// Botões e lista são o mesmo menu, tocável. A diferença que importa na tela é
// o teto do WhatsApp: 3 botões, 10 linhas de lista. Passar disso não dá erro
// — a mensagem chega sem desenhar nada no aparelho de quem recebe.
const MenuTocavel = ({ data, selected, icon, titulo, teto, dica }) => {
  const opcoes = Array.isArray(data.options) ? data.options : [];
  const excedeu = opcoes.length > teto;

  return (
    <>
      <Handle type="target" position={Position.Top} aria-label="Entrada" />
      <NodeShell
        selected={selected}
        accent={ACENTOS.pergunta}
        icon={icon}
        title={titulo}
      >
        <div className="mt-0.5 rounded-lg bg-muted/60 px-2.5 py-1.5">
          <p className="truncate text-xs leading-relaxed text-muted-foreground">
            {data.message || "Clique para escrever a pergunta"}
          </p>
        </div>

        {opcoes.length === 0 ? (
          <p className="mt-1 px-2.5 py-1.5 text-xs text-muted-foreground">
            {dica}
          </p>
        ) : (
          opcoes.map((opcao, i) => (
            <Saida
              key={`${opcao.number}-${i}`}
              id={`opt-${opcao.number}`}
              texto={`${opcao.number}. ${opcao.label || "sem texto"}`}
            />
          ))
        )}

        {excedeu && (
          <p className="mt-1 px-2.5 py-1 text-xs text-destructive">
            O WhatsApp mostra só {teto}; o resto não aparece.
          </p>
        )}

        <Saida id="fallback" texto="Se desistir" />
      </NodeShell>
    </>
  );
};

export const ButtonsNode = (props) => (
  <MenuTocavel
    {...props}
    icon={MousePointerClick}
    titulo="Botões"
    teto={3}
    dica="Nenhum botão ainda"
  />
);

export const ListNode = (props) => (
  <MenuTocavel
    {...props}
    icon={List}
    titulo="Lista"
    teto={10}
    dica="Nenhum item ainda"
  />
);

// Fim: não tem saída. Depois dele o robô se cala neste atendimento até que
// alguém o encerre.
export const EndNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.atendimento}
      icon={Flag}
      title="Fim"
      summary={
        data.closeTicket
          ? "Encerra o atendimento e para o robô"
          : "Para o robô até o atendimento ser encerrado"
      }
    />
  </>
);

export const QuestionNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.pergunta}
      icon={HelpCircle}
      title="Pergunta"
      summary={
        data.variable
          ? `Resposta em {{${data.variable}}}`
          : "Sem nome de variável"
      }
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const MediaNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.fala}
      icon={Image}
      title="Enviar mídia"
      summary={data.url || "Sem arquivo"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const RandomizerNode = ({ data, selected }) => {
  const a = Number(data.percent ?? 50);

  return (
    <>
      <Handle type="target" position={Position.Top} aria-label="Entrada" />
      <NodeShell
        selected={selected}
        accent={ACENTOS.caminho}
        icon={Shuffle}
        title="Sorteio"
      >
        <Saida id="a" texto={`Caminho A · ${a}%`} />
        <Saida id="b" texto={`Caminho B · ${100 - a}%`} />
      </NodeShell>
    </>
  );
};

export const AssignUserNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.atendimento}
      icon={UserCheck}
      title="Atribuir atendente"
      summary={data.userName || "Escolha o atendente"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

const NOME_DO_STATUS = {
  open: "Atendendo",
  pending: "Aguardando",
  closed: "Resolvido",
};

export const TicketStatusNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.atendimento}
      icon={CircleCheck}
      title="Situação do atendimento"
      summary={NOME_DO_STATUS[data.status] || "Escolha a situação"}
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

// Ações na conversa que não são mensagem. Um componente só para as quatro:
// mudam apenas ícone, título e resumo.
const acaoNaConversa = (icon, title, resumo) => {
	const Componente = ({ data, selected }) => (
		<>
			<Handle type="target" position={Position.Top} aria-label="Entrada" />
			<NodeShell
				selected={selected}
				accent={ACENTOS.atendimento}
				icon={icon}
				title={title}
				summary={resumo(data)}
			/>
			<Handle type="source" position={Position.Bottom} aria-label="Saída" />
		</>
	);

	Componente.displayName = title;
	return Componente;
};

export const TypingNode = acaoNaConversa(
	Clock,
	"Digitando",
	(data) => `Por ${data.seconds || 3}s`
);

export const MarkReadNode = acaoNaConversa(
	CircleCheck,
	"Marcar como lida",
	() => "Zera o não lido da conversa"
);

export const ArchiveNode = acaoNaConversa(
	Layers,
	"Arquivar conversa",
	(data) => (data.desfazer ? "Desarquivar" : "Arquivar")
);

export const BlockNode = acaoNaConversa(
	Ban,
	"Bloquear contato",
	(data) => (data.desfazer ? "Desbloquear" : "Bloquear")
);

export const BoardNode = ({ data, selected }) => (
	<>
		<Handle type="target" position={Position.Top} aria-label="Entrada" />
		<NodeShell
			selected={selected}
			accent={ACENTOS.atendimento}
			icon={Columns3}
			title="Mover no quadro"
			summary={data.laneName || "Escolha a raia"}
		/>
		<Handle type="source" position={Position.Bottom} aria-label="Saída" />
	</>
);

const ROTULO_DO_ITEM = {
  text: "Texto",
  image: "Imagem",
  audio: "Áudio",
  video: "Vídeo",
  document: "Documento",
  delay: "Espera",
};

const ICONE_DO_ITEM = {
  text: MessageSquare,
  image: Image,
  audio: Mic,
  video: Video,
  document: FileText,
  delay: Clock,
};

// Conteúdo: o cartão do Typebot. Várias falas empilhadas dentro de um nó só,
// porque uma boa-vinda costuma ser "texto + imagem + texto" e um nó por
// mensagem enchia a tela de caixinhas para dizer uma coisa só.
export const ContentNode = ({ data, selected }) => {
  const itens = data.items || [];

  return (
    <>
      <Handle type="target" position={Position.Top} aria-label="Entrada" />
      <div
        className={cn(
          "min-w-[220px] rounded-xl border bg-card shadow-xs transition-shadow",
          selected ? "ring-2 ring-primary ring-offset-2" : "hover:shadow-md"
        )}
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Layers className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold">Conteúdo</span>
        </div>

        <div className="space-y-1 p-2">
          {itens.length === 0 && (
            <p className="px-1 py-2 text-xs text-muted-foreground">
              Nenhuma mensagem ainda
            </p>
          )}
          {itens.map((item, i) => {
            const Icone = ICONE_DO_ITEM[item.kind] || MessageSquare;
            const resumo =
              item.kind === "delay"
                ? `${item.seconds || 0}s`
                : item.value || ROTULO_DO_ITEM[item.kind] || "";

            return (
              <div
                key={i}
                className="flex items-center gap-2 rounded-md bg-muted/60 px-2 py-1.5"
              >
                <Icone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate text-xs">{resumo}</span>
              </div>
            );
          })}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} aria-label="Saída" />
    </>
  );
};

const NOME_DO_SERVICO = {
  http: "Requisição HTTP",
  sheets: "Google Sheets",
  asaas: "Asaas (cobrança)",
  calendar: "Google Calendar",
};

export const IntegrationNode = ({ data, selected }) => {
  const servico = data.service || "http";

  const resumo = {
    http: data.url ? `${data.method || "GET"} ${data.url}` : "Sem URL",
    sheets:
      data.operation === "append" ? "Gravar linha" : "Ler intervalo",
    asaas: data.operation === "status" ? "Consultar cobrança" : "Criar cobrança",
    calendar:
      data.operation === "list"
        ? "Ler eventos"
        : data.operation === "freebusy"
          ? "Consultar disponibilidade"
          : "Criar evento",
  }[servico];

  return (
    <>
      <Handle type="target" position={Position.Top} aria-label="Entrada" />
      <NodeShell
        selected={selected}
        accent={ACENTOS.integracao}
        icon={Plug}
        title={NOME_DO_SERVICO[servico] || "Integração"}
        summary={resumo}
      />
      <Handle type="source" position={Position.Bottom} aria-label="Saída" />
    </>
  );
};

export const SetVariableNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.caminho}
      icon={Variable}
      title="Definir variável"
      summary={
        data.variable
          ? `${data.variable} = ${data.value || "(vazio)"}`
          : "Clique para dar um nome"
      }
    />
    <Handle type="source" position={Position.Bottom} aria-label="Saída" />
  </>
);

export const JumpNode = ({ data, selected }) => (
  <>
    <Handle type="target" position={Position.Top} aria-label="Entrada" />
    <NodeShell
      selected={selected}
      accent={ACENTOS.caminho}
      icon={CornerUpLeft}
      title="Pular para"
      summary={data.targetLabel || "Escolha o bloco de destino"}
    />
  </>
);

// Os mapas `nodeTypes`, `NODE_DEFAULTS`, `NODE_LABELS` e a paleta saíram daqui:
// os quatro descreviam o mesmo nó em lugares diferentes e saíam de sincronia em
// silêncio. Agora cada nó é uma entrada em `catalog.jsx` e os mapas derivam
// dela. Reexportados aqui para não quebrar quem já importava deste arquivo.
export {
  NODE_CATALOG,
  NODE_DEFAULTS,
  NODE_LABELS,
  PALETTE,
  acharNo,
  nodeTypes,
} from "./catalog";
